import React from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import Dropdown from '@hobs/components/Dropdown';
import DateInput from '@hobs/components/DateInput';
import { Formik, Form, Field } from 'formik';
import Input from '@hobs/components/Input';
import { RaisedButton } from '@hobs/components/Buttons';
import PropTypes from 'prop-types';
import Switch from '@hobs/components/Switch';
import './UserForm.scss';

import { DEFAULT_DAYS_UNTIL_USER_EXPIRE } from '@hobs/utils/constants';

const UserForm = ({
  userSchema,
  unitOptions,
  partyId,
  roleOptions,
  user,
  onSubmit,
  enableReinitialize,
  isCreation
}) => {
  const { t } = useTranslation();
  const defaultLoginExpirationDate = new Date();
  defaultLoginExpirationDate.setDate(
    defaultLoginExpirationDate.getDate() + DEFAULT_DAYS_UNTIL_USER_EXPIRE
  );

  const onEmailValueChanged = (event, setFieldValue, useEmailAsUsername) => {
    if (event && event.target && useEmailAsUsername) {
      const value = event.target.value;
      setFieldValue('username', value);
    }
  };

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={userSchema}
      enableReinitialize={enableReinitialize}
      initialValues={
        user
          ? user
          : {
              partyId: partyId,
              firstName: '',
              lastName: '',
              email: '',
              loginExpirationDate: defaultLoginExpirationDate,
              roleId: '',
              isDeleted: false,
              unitId: '',
              actions: {
                canEditUnit: true,
                canEditRole: true,
                canDeleteUser: true
              },
              useEmailAsUsername: true
            }
      }
      onSubmit={onSubmit}
    >
      {({ setFieldValue, isSubmitting, values }) => (
        <Form>
          <div className="user-create--dialog">
            <div className="user-create--dialog--form">
              <div className="user-create--dialog--row">
                <div className="user-create--dialog--column">
                  <Field
                    name="unitId"
                    setFieldValue={setFieldValue}
                    component={Dropdown}
                    placeholder={t('Select element')}
                    label={t('Assigned to')}
                    disabled={!values.actions.canEditUnit}
                  >
                    <option value="">
                      {t(unitOptions ? 'Select element' : 'There are no Units')}
                    </option>
                    {unitOptions}
                  </Field>
                </div>
                <div className="user-create--dialog--column">
                  <Field
                    name="firstName"
                    setFieldValue={setFieldValue}
                    component={Input}
                    disabled={values.isDeleted}
                  >
                    {t('First name')}
                  </Field>
                </div>
              </div>
              <div className="user-create--dialog--row">
                <div className="user-create--dialog--column">
                  <Field
                    name="roleId"
                    setFieldValue={setFieldValue}
                    component={Dropdown}
                    placeholder={t('Select role')}
                    label={t('Role')}
                    disabled={!values.actions.canEditRole}
                  >
                    <option value="">{t('Select role')}</option>
                    {roleOptions}
                  </Field>
                </div>
                <div className="user-create--dialog--column">
                  <Field
                    name="lastName"
                    setFieldValue={setFieldValue}
                    component={Input}
                    disabled={values.isDeleted}
                  >
                    {t('Last name')}
                  </Field>
                </div>
              </div>
              <div className="user-create--dialog--row">
                <div className="user-create--dialog--column">
                  <Field
                    name="loginExpirationDate"
                    setFieldValue={setFieldValue}
                    component={DateInput}
                    label={t('Login expiration date')}
                    disabled={values.isDeleted}
                  >
                    {t('Login expiration date')}
                  </Field>
                </div>
                <div className="user-create--dialog--column">
                  <Field
                    name="email"
                    setFieldValue={setFieldValue}
                    component={Input}
                    disabled={values.isDeleted}
                    onChange={event =>
                      onEmailValueChanged(
                        event,
                        setFieldValue,
                        values.useEmailAsUsername
                      )
                    }
                  >
                    {t('Email')}
                  </Field>
                </div>
              </div>
              <div className="user-create--dialog--row">
                <div className="user-create--dialog--column">
                  <Field
                    name="phone"
                    setFieldValue={setFieldValue}
                    component={Input}
                    disabled={values.isDeleted}
                  >
                    {t('Phone')}
                  </Field>
                </div>
                <div className="user-create--dialog--column">
                  <Field
                    name="username"
                    setFieldValue={setFieldValue}
                    component={Input}
                    disabled={values.isDeleted || values.useEmailAsUsername}
                  >
                    {t('Username')}
                  </Field>
                </div>
              </div>
              {isCreation && (
                <div className="user-create--dialog--row">
                  <div className="user-create--dialog--column"></div>
                  <div className="user-create--dialog--column">
                    <div className="user-create--dialog--switch">
                      <Field
                        name="useEmailAsUsername"
                        component={Switch}
                        setFieldValue={setFieldValue}
                        defaultChecked={values.useEmailAsUsername}
                        label={t('Use email as username')}
                        disabled={values.isDeleted}
                        onChange={value => {
                          if (value) {
                            setFieldValue('username', values.email);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="user-create--dialog--submit-button">
              <RaisedButton
                type="submit"
                disabled={isSubmitting || values.isDeleted}
              >
                {t('Save')}
              </RaisedButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

UserForm.propTypes = {
  setFieldValue: PropTypes.func,
  t: PropTypes.func,
  roleOptions: PropTypes.array,
  unitOptions: PropTypes.array,
  user: PropTypes.object,
  partyId: PropTypes.string,
  userSchema: PropTypes.object,
  enableReinitialize: PropTypes.bool,
  onSubmit: PropTypes.func,
  isCreation: PropTypes.bool
};

export default withTranslation()(UserForm);
